import { tap } from 'rxjs/operators';

import { inject, Injectable } from '@angular/core';

import {
  ActionContext,
  Actions,
  ActionStatus,
  createSelector,
  getActionTypeFromInstance,
  NgxsOnInit,
  State,
  StateContext,
  StateToken,
} from '@ngxs/store';

import { assertArray } from '@shared/utilities/array.utilities';
import { isDeepEqual, isPrimitive } from '@shared/utilities/object.utilities';

export type ActionsStateModel = Record<string, any[]>;

const ACTIONS_STATE = new StateToken<ActionsStateModel>('actions');

@Injectable()
@State<ActionsStateModel>({
  name: ACTIONS_STATE,
  defaults: {},
})
export class ActionsState implements NgxsOnInit {
  private actions$ = inject(Actions);

  static whileAction(actions: any | any[], withArguments?: object) {
    actions = assertArray(actions);

    return createSelector([ActionsState], (state: ActionsStateModel) =>
      actions.some(
        (action) =>
          assertArray(state[getActionTypeFromInstance(action)]).filter((dispatched) => {
            if (withArguments) {
              if (typeof withArguments === 'function') {
                return withArguments(dispatched);
              } else if (typeof withArguments === 'object') {
                return Object.entries(withArguments).every(([key, value]) => dispatched[key] === value);
              }
            }

            return true;
          }).length > 0,
      ),
    );
  }

  static completedAction(actions: any | any[]) {
    actions = Array.isArray(actions) ? actions : [actions];

    return createSelector([ActionsState], (state) =>
      actions.every((action) => state[getActionTypeFromInstance(action)]?.length === 0),
    );
  }

  static dispatchedAction(action: any) {
    return createSelector([ActionsState], (state) => assertArray(state[getActionTypeFromInstance(action)]));
  }

  ngxsOnInit({ patchState, getState }: StateContext<ActionsStateModel>): void {
    this.actions$
      .pipe(
        tap((ctx: ActionContext) => {
          const type = getActionTypeFromInstance(ctx.action);

          if (!type) {
            return;
          }

          let actions = [...assertArray(getState()[type])];

          const action = Object.entries(ctx.action).reduce(
            (filtered, [key, value]) => (isPrimitive(value) ? { ...filtered, [key]: value } : filtered),
            {},
          );

          if (ctx.status === ActionStatus.Dispatched) {
            actions.push(action);
          } else {
            actions = actions.filter((target) => !isDeepEqual(target, action));
          }

          patchState({ [type]: actions });
        }),
      )
      .subscribe();
  }
}
